<script setup>
// Presentational dialogs (cancel item + add item) for the order item actions.
// Receives the state/handlers produced by useOrderItemActions (v-bind the whole object).
import { computed } from 'vue';

defineOptions({ inheritAttrs: false });

const props = defineProps({
    cancelItemState: { type: Object, required: true },
    bulkCancelState: { type: Object, required: true },
    addItemState: { type: Object, required: true },
    siteSuggestions: { type: Array, default: () => [] },
    siteOffers: { type: Array, default: () => [] },
    confirmCancelItem: { type: Function, required: true },
    confirmBulkCancel: { type: Function, required: true },
    onSiteSelected: { type: Function, required: true },
    offerLabel: { type: Function, required: true },
    searchSites: { type: Function, required: true },
    submitAddItem: { type: Function, required: true },
});

// How far the item being canceled has progressed, so we can warn the admin that
// canceling a published / already-requested item does NOT undo the seller's work.
const cancelStage = computed(() => {
    const it = props.cancelItemState?.item;
    if (!it) return null;
    if (it.link || it.live_link_confirmed_at || it.link_status === 'PUBLISHED') return 'published';
    if (it.link_status === 'PROCESSING') return 'sent';
    if (it.link_status === 'SUBMITTED') return 'submitted';
    return null;
});
</script>

<template>
    <!-- Cancel single item dialog -->
    <Dialog
        v-model:visible="cancelItemState.visible"
        modal
        :header="$t('Cancel this item?')"
        :style="{ width: '460px' }"
        :breakpoints="{ '641px': '95vw' }"
    >
        <div class="space-y-3">
            <p v-if="cancelItemState.item" class="text-sm text-surface-600">
                {{ cancelItemState.item.site_url || '-' }}
            </p>
            <Message v-if="cancelStage === 'published'" severity="error" :closable="false" class="text-sm">
                {{ $t('This item is already PUBLISHED (the link is live). Canceling it will not remove the published link, and you may still owe the seller.') }}
            </Message>
            <Message v-else-if="cancelStage === 'sent'" severity="warn" :closable="false" class="text-sm">
                {{ $t('Publication of this item was already requested from the seller. Canceling may not stop the publication, and you may still owe the seller.') }}
            </Message>
            <Message v-else-if="cancelStage === 'submitted'" severity="info" :closable="false" class="text-sm">
                {{ $t('The content of this item was already approved and is ready to be sent to the seller.') }}
            </Message>
            <div>
                <label class="block text-xs font-medium text-surface-500 uppercase mb-1">
                    {{ $t('Cancellation reason') }}
                </label>
                <Textarea
                    v-model="cancelItemState.reason"
                    rows="3"
                    class="w-full"
                    :placeholder="$t('Optional')"
                />
            </div>
        </div>
        <template #footer>
            <Button :label="$t('Back')" text severity="secondary" :disabled="cancelItemState.processing" @click="cancelItemState.visible = false" />
            <Button :label="$t('Cancel item')" severity="danger" :loading="cancelItemState.processing" :disabled="cancelItemState.processing" @click="confirmCancelItem" />
        </template>
    </Dialog>

    <!-- Bulk cancel items dialog (shared reason, one consolidated email) -->
    <Dialog
        v-model:visible="bulkCancelState.visible"
        modal
        :header="$t('Cancel selected items?')"
        :style="{ width: '460px' }"
        :breakpoints="{ '641px': '95vw' }"
    >
        <div class="space-y-3">
            <p class="text-sm text-surface-600">
                {{ $t('{count} item(s) will be canceled. The client will receive a single notification email.').replace('{count}', bulkCancelState.ids?.length || 0) }}
            </p>
            <div>
                <label class="block text-xs font-medium text-surface-500 uppercase mb-1">
                    {{ $t('Cancellation reason') }}
                </label>
                <Textarea
                    v-model="bulkCancelState.reason"
                    rows="3"
                    class="w-full"
                    :placeholder="$t('Optional')"
                />
            </div>
        </div>
        <template #footer>
            <Button :label="$t('Back')" text severity="secondary" :disabled="bulkCancelState.processing" @click="bulkCancelState.visible = false" />
            <Button :label="$t('Cancel items')" severity="danger" :loading="bulkCancelState.processing" :disabled="bulkCancelState.processing" @click="confirmBulkCancel" />
        </template>
    </Dialog>

    <!-- Add item dialog -->
    <Dialog
        v-model:visible="addItemState.visible"
        modal
        :header="$t('Add item')"
        :style="{ width: '520px' }"
        :breakpoints="{ '641px': '95vw' }"
    >
        <div class="space-y-4">
            <div>
                <label class="block text-xs font-medium text-surface-500 uppercase mb-1">{{ $t('Site') }}</label>
                <AutoComplete
                    v-model="addItemState.site"
                    :suggestions="siteSuggestions"
                    @complete="searchSites"
                    @item-select="onSiteSelected($event.value)"
                    optionLabel="url"
                    :minLength="2"
                    :forceSelection="true"
                    :placeholder="$t('Search for a site...')"
                    class="w-full"
                    inputClass="w-full"
                />
                <small class="text-surface-400">{{ $t('Type a domain or URL to search.') }}</small>
            </div>
            <div>
                <label class="block text-xs font-medium text-surface-500 uppercase mb-1">{{ $t('Type') }}</label>
                <Select
                    v-model="addItemState.type_id"
                    :options="siteOffers"
                    :optionLabel="offerLabel"
                    optionValue="type_id"
                    :disabled="siteOffers.length === 0"
                    :placeholder="siteOffers.length === 0 ? $t('No niches available') : $t('Select a type')"
                    class="w-full"
                />
            </div>
            <div>
                <label class="block text-xs font-medium text-surface-500 uppercase mb-1">{{ $t('Observations') }}</label>
                <Textarea
                    v-model="addItemState.observations"
                    rows="3"
                    class="w-full"
                    :placeholder="$t('Optional')"
                />
            </div>
        </div>
        <template #footer>
            <Button :label="$t('Back')" text severity="secondary" :disabled="addItemState.processing" @click="addItemState.visible = false" />
            <Button :label="$t('Add item')" icon="pi pi-plus" :loading="addItemState.processing" :disabled="addItemState.processing" @click="submitAddItem" />
        </template>
    </Dialog>
</template>
